import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { BarList, Panel } from "@/components/analytics-ui";
import { GscChart } from "@/components/gsc-chart";
import { DataTable } from "@/components/data-table";
import { SkAnalyticsPage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { enumerateDays } from "@shared/date-range";
import type { SearchConsolePage } from "@shared/pages/search-console";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// Search Console: organic Google performance. "Last N days" end yesterday
// (Search Console has no same-day data), like the real GSC UI. The overview uses the
// GSC metric-card pattern (cards toggle chart series); queries and pages are
// sortable, paginated tables.

export const Route = createFileRoute("/_app/search-console")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<SearchConsolePage>("search-console", deps));
  },
  component: SearchConsoleRoute,
});

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "queries", label: "Search queries" },
  { key: "pages", label: "Top pages" },
];

function SearchConsoleRoute() {
  useTitle("Search Console");
  const search = Route.useSearch();
  return (
    <PageQuery<SearchConsolePage> name="search-console" search={search} skeleton={<SkAnalyticsPage />}>
      {(page) => <SearchConsole page={page} />}
    </PageQuery>
  );
}

function SearchConsole({ page }: { page: SearchConsolePage }) {
  const { configured, report, presets, range, tab } = page;

  if (!configured || !report) {
    return (
      <div className="page-shell">
        <PageHeader title="Search Console" />
        <Panel title={!configured ? "Not connected" : "No data"} className="mt-5">
          <p className="text-sm text-muted">
            {!configured
              ? "Search numbers show here once Digital King connects Search Console."
              : "Search Console numbers aren't available for this site right now."}
          </p>
        </Panel>
      </div>
    );
  }

  const days = enumerateDays(range);
  const dailyByDate = new Map(report.daily.map((d) => [d.date, d]));
  const metricCols = [
    { key: "clicks", label: "Clicks", format: "num", align: "right", total: "sum" },
    { key: "impressions", label: "Impressions", format: "num", align: "right", total: "sum" },
    { key: "ctr", label: "CTR", format: "pct", align: "right", total: { of: "clicks", per: "impressions" } },
    { key: "position", label: "Avg position", format: "pos", align: "right" },
  ] as const;

  return (
    <div className="page-shell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title={tab === "overview" ? "Search Console" : (TABS.find((t) => t.key === tab)?.label ?? "Search Console")}
          info="Free Google search results. Google reports through yesterday."
        />
        <DateRangeFilter presets={presets} defaultKey="30d" />
      </div>

      {/* The four GSC metric cards stay on every tab; the chart is Overview-only. */}
      <div className="mt-5">
        <GscChart
          labels={days.map((d) => d.label)}
          clicks={days.map(({ date }) => dailyByDate.get(date)?.clicks ?? 0)}
          impressions={days.map(({ date }) => dailyByDate.get(date)?.impressions ?? 0)}
          ctr={days.map(({ date }) => dailyByDate.get(date)?.ctr ?? 0)}
          position={days.map(({ date }) => dailyByDate.get(date)?.position ?? 0)}
          totals={report.totals}
          chart={tab === "overview"}
        />
      </div>

      {tab === "overview" && (
        <>
          <div className="mt-4">
            <Panel title="Devices">
              <BarList
                items={report.devices.map((d) => ({
                  label: d.device.charAt(0).toUpperCase() + d.device.slice(1),
                  count: d.clicks,
                  sub: `${d.impressions.toLocaleString("en-US")} impressions`,
                }))}
                emptyText="No device data in this range."
              />
            </Panel>
          </div>
        </>
      )}

      {tab === "queries" && (
        <Panel title="What people searched on Google (organic)" className="mt-5">
          <DataTable
            columns={[{ key: "query", label: "Search", format: "text" }, ...metricCols]}
            rows={report.queries}
            defaultSort="clicks"
          />
          <p className="mt-2 text-xs text-muted">
            Avg position is where the site typically appears in Google&apos;s results for that
            search (1 = top result). These clicks are free.
          </p>
        </Panel>
      )}

      {tab === "pages" && (
        <Panel title="Pages earning Google traffic" className="mt-5">
          <DataTable
            columns={[{ key: "page", label: "Page", format: "text" }, ...metricCols]}
            rows={report.pages}
            defaultSort="clicks"
          />
        </Panel>
      )}
    </div>
  );
}
